import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Video,
  Upload,
  Play,
  Download,
  Edit,
  Trash2,
  Eye,
  Users,
  Clock,
  FileVideo,
  Filter,
  Search,
  MoreVertical,
  AlertCircle,
  CheckCircle,
  Loader2,
  HardDrive,
  TrendingUp,
  Star
} from 'lucide-react';

// Hivetivity UI Components
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AnimatedThumbnail } from '@/components/videos/AnimatedThumbnail';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { AdminPageScaffold } from '@/components/core';
import VideoUploadDialog from '@/components/videos/VideoUploadDialog';
import VideoEditDialog from '@/components/videos/VideoEditDialog';

// Services and Types
import { HiveShareService, HiveShareVideo, HiveShareUsageStatus } from '@/services/hiveShareService';
import { getFamilies } from '@/services/familyServices/familyService';
import { Family } from '@/types/familyTypes';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';

// Cloudflare Stream SDK type definition for admin
declare global {
  interface Window {
    Stream: (element: HTMLElement) => {
      play: () => Promise<void>;
      pause: () => void;
      muted: boolean;
      currentTime: number;
      volume: number;
      addEventListener: (event: string, callback: () => void) => void;
      removeEventListener: (event: string, callback: () => void) => void;
    };
  }
}

// Admin Video Player Component
interface AdminVideoPlayerProps {
  video: HiveShareVideo;
  controls?: boolean;
  autoplay?: boolean;
  onPlay?: () => void;
  className?: string;
}

const AdminVideoPlayer: React.FC<AdminVideoPlayerProps> = ({
  video,
  controls = true,
  autoplay = false,
  onPlay,
  className
}) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const playerRef = React.useRef<any>(null);
  const [isSDKLoaded, setIsSDKLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [signedIframeUrl, setSignedIframeUrl] = React.useState<string | null>(null);

  console.log('📺 AdminVideoPlayer received video object:', {
    video_id: video.id,
    cloudflare_stream_id: video.cloudflare_stream_id,
    title: video.title,
    playback_url: video.playback_url,
    status: video.upload_status
  });

  // Get signed iframe URL from server
  React.useEffect(() => {
    let isMounted = true;

    const getSignedIframeUrl = async () => {
      try {
        console.log('🔐 Requesting signed iframe URL for video:', {
          cloudflare_stream_id: video.cloudflare_stream_id,
          video_id: video.id,
          title: video.title
        });
        
        const { data, error } = await supabase.functions.invoke('hiveshare-signed-url', {
          body: {
            videoId: video.cloudflare_stream_id,
            type: 'iframe',
            expiresIn: 3600 // 1 hour
          }
        });

        if (error) {
          throw new Error(`Failed to get signed URL: ${error.message}`);
        }

        if (!data.success) {
          throw new Error(`Signed URL error: ${data.error}`);
        }

        if (isMounted) {
          console.log('🔐 Received signed iframe URL:', data.signedUrl);
          setSignedIframeUrl(data.signedUrl);
        }
      } catch (error) {
        console.error('🔐 Failed to get signed iframe URL:', error);
        if (isMounted) {
          setHasError(true);
        }
      }
    };

    if (video.cloudflare_stream_id) {
      getSignedIframeUrl();
    }

    return () => {
      isMounted = false;
    };
  }, [video.cloudflare_stream_id]);


  // Load Cloudflare Stream SDK
  React.useEffect(() => {
    if (window.Stream) {
      setIsSDKLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
    script.onload = () => setIsSDKLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Initialize player when SDK is loaded and iframe is ready
  React.useEffect(() => {
    console.log('🎮 Player init check:', {
      isSDKLoaded,
      hasIframe: !!iframeRef.current,
      hasStream: !!window.Stream,
      streamId: video.cloudflare_stream_id,
      hasSignedUrl: !!signedIframeUrl,
      isLoading
    });

    if (isSDKLoaded && iframeRef.current && window.Stream && video.cloudflare_stream_id && signedIframeUrl && !isLoading) {
      try {
        playerRef.current = window.Stream(iframeRef.current);
        
        // Set up event listeners
        if (onPlay) {
          playerRef.current.addEventListener('play', onPlay);
        }

        // Handle autoplay with potential mute fallback
        if (autoplay) {
          playerRef.current.play().catch(() => {
            // If autoplay fails, try with mute
            playerRef.current.muted = true;
            playerRef.current.play().catch(() => {
              console.warn('Autoplay failed even with mute');
            });
          });
        }
      } catch (error) {
        console.error('Failed to initialize Cloudflare Stream player:', error);
      }
    }

    return () => {
      if (playerRef.current && onPlay) {
        try {
          playerRef.current.removeEventListener('play', onPlay);
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [isSDKLoaded, video.cloudflare_stream_id, signedIframeUrl, autoplay, onPlay]);

  if (!video?.cloudflare_stream_id) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-gray-900 ${className}`}>
        <div className="text-white text-center">
          <Video className="h-12 w-12 mx-auto mb-2" />
          <p>Video not available</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-gray-900 ${className}`}>
        <div className="text-white text-center p-4">
          <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Failed to load video</p>
          <p className="text-xs text-gray-400 mt-1">Please try refreshing the page</p>
          <div className="mt-3 text-xs text-gray-500 border-t border-gray-700 pt-2">
            <p className="font-mono break-all">Video ID: {video.cloudflare_stream_id}</p>
            <p className="font-mono break-all">Signed URL: {signedIframeUrl ? '✅ Generated' : '❌ Failed'}</p>
            <p className="text-xs mt-1">Check console for detailed error messages</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {(isLoading || !signedIframeUrl) && !hasError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
          <div className="text-white text-center">
            <Video className="h-12 w-12 mx-auto mb-2 animate-pulse" />
            <p className="text-sm">{signedIframeUrl ? 'Loading video...' : 'Generating signed URL...'}</p>
          </div>
        </div>
      )}
      {signedIframeUrl ? (
        <iframe
          ref={iframeRef}
          src={signedIframeUrl}
          className="w-full h-full"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          style={{
            border: 'none',
            width: '100%',
            height: '100%',
          }}
          onLoad={() => {
            console.log('🎬 Signed iframe loaded successfully');
            console.log('🎬 Using signed URL:', signedIframeUrl);
            setIsLoading(false);
          }}
          onError={() => {
            console.error('🎬 Signed iframe failed to load');
            setHasError(true);
            setIsLoading(false);
          }}
        />
      ) : hasError ? null : (
        <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
          <div className="text-center">
            <Video className="h-8 w-8 mx-auto mb-2 animate-spin" />
            <p className="text-sm">Generating signed URL...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } 
  }
};

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3 }
  }
};

interface VideoManagementDashboardV2Props {
  organizationId: string;
}

export const VideoManagementDashboardV2: React.FC<VideoManagementDashboardV2Props> = ({
  organizationId
}) => {
  const [videos, setVideos] = useState<HiveShareVideo[]>([]);
  const [usageStatus, setUsageStatus] = useState<HiveShareUsageStatus | null>(null);
  const [families, setFamilies] = useState<Family[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  
  // Video player modal state
  const [selectedVideo, setSelectedVideo] = useState<HiveShareVideo | null>(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  
  // Video edit modal state
  const [editingVideo, setEditingVideo] = useState<HiveShareVideo | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Store signed tokens for thumbnails (keyed by video ID)
  const [signedTokens, setSignedTokens] = useState<Record<string, string>>({});
  
  const { toast } = useToast();

  // Generate signed tokens for video thumbnails
  const generateSignedTokens = async (videos: HiveShareVideo[]) => {
    const tokens: Record<string, string> = {};
    
    // Generate tokens for all videos in parallel
    const tokenPromises = videos.map(async (video) => {
      try {
        const response = await HiveShareService.getSignedVideoUrl(video.cloudflare_stream_id, 'iframe');
        if (response.success && response.signedUrl) {
          // Extract token from signed URL - it's the part between the domain and /iframe
          const urlMatch = response.signedUrl.match(/\/([^\/]+)\/iframe$/);
          if (urlMatch) {
            tokens[video.cloudflare_stream_id] = urlMatch[1];
          }
        }
      } catch (error) {
        console.warn(`Failed to get signed token for video ${video.cloudflare_stream_id}:`, error);
        // Continue without token for this video
      }
    });
    
    await Promise.all(tokenPromises);
    setSignedTokens(tokens);
  };

  // Load data
  useEffect(() => {
    loadDashboardData();
  }, [organizationId]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [videosData, usageData, familiesData] = await Promise.all([
        HiveShareService.getOrganizationVideos(organizationId),
        HiveShareService.getUsageStatus(organizationId),
        getFamilies(organizationId)
      ]);

      setVideos(videosData);
      setFamilies(familiesData);
      setUsageStatus(usageData);
      
      // Generate signed tokens for thumbnails
      await generateSignedTokens(videosData);
    } catch (err) {
      console.error('Failed to load video data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load videos'));
    } finally {
      setIsLoading(false);
    }
  };

  // Filter videos
  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         statusFilter === 'favorites' && video.is_favorite ||
                         video.upload_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get status badge variant
  const getStatusBadge = (status: HiveShareVideo['upload_status']) => {
    const config = {
      uploading: { variant: 'secondary' as const, text: 'Uploading', icon: Loader2 },
      processing: { variant: 'secondary' as const, text: 'Processing', icon: Clock },
      ready: { variant: 'default' as const, text: 'Ready', icon: CheckCircle },
      error: { variant: 'destructive' as const, text: 'Error', icon: AlertCircle },
      deleted: { variant: 'outline' as const, text: 'Deleted', icon: Trash2 }
    };

    const { variant, text, icon: Icon } = config[status];
    
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {text}
      </Badge>
    );
  };

  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // Handle upload success
  const handleUploadSuccess = (videoId: string) => {
    setShowUploadDialog(false);
    loadDashboardData(); // Refresh data
    toast({
      title: 'Video uploaded successfully!',
      description: 'Your video is being processed and will be available soon.',
    });
  };

  // Video player functions
  const handlePlayVideo = (video: HiveShareVideo) => {
    console.log('🎬 handlePlayVideo called with video:', {
      id: video.id,
      cloudflare_stream_id: video.cloudflare_stream_id,
      title: video.title
    });
    setSelectedVideo(video);
    setIsPlayerModalOpen(true);
  };

  const handleCloseVideoPlayer = () => {
    setIsPlayerModalOpen(false);
    setSelectedVideo(null);
  };

  // Video edit functions
  const handleEditVideo = (video: HiveShareVideo) => {
    setEditingVideo(video);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingVideo(null);
  };

  const handleSaveVideoEdit = async (videoId: string, updates: any) => {
    try {
      await HiveShareService.updateVideo(videoId, updates);
      toast({
        title: 'Video updated',
        description: 'The video details have been saved successfully.',
      });
      // Refresh data to show updates
      await loadDashboardData();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: 'Failed to update video. Please try again.',
        variant: 'destructive',
      });
      throw err; // Re-throw to let the dialog handle loading state
    }
  };

  // Handle video download
  const handleDownloadVideo = async (video: HiveShareVideo) => {
    try {
      // Generate a signed download URL for security
      const response = await HiveShareService.getSignedVideoUrl(video.cloudflare_stream_id, 'download');
      
      if (response.success && response.signedUrl) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = response.signedUrl;
        link.download = `${video.title}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: 'Download started',
          description: 'The video download has been initiated.',
        });
      } else {
        throw new Error('Failed to generate download URL');
      }
    } catch (err) {
      console.error('Download failed:', err);
      toast({
        title: 'Download failed',
        description: 'Failed to download video. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle video favorite toggle
  const handleToggleFavorite = async (video: HiveShareVideo) => {
    try {
      const result = await HiveShareService.toggleVideoFavorite(video.id);
      
      // Update the local state to reflect the change
      setVideos(prevVideos => 
        prevVideos.map(v => 
          v.id === video.id 
            ? { ...v, is_favorite: result.isFavorite }
            : v
        )
      );
      
      toast({
        title: result.isFavorite ? 'Added to favorites' : 'Removed from favorites',
        description: result.isFavorite 
          ? 'Video has been added to your favorites.'
          : 'Video has been removed from your favorites.',
      });
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      toast({
        title: 'Failed to update favorite',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle video delete
  const handleDeleteVideo = async (videoId: string) => {
    try {
      await HiveShareService.deleteVideo(videoId);
      toast({
        title: 'Video deleted',
        description: 'The video has been removed successfully.',
      });
      // Refresh data to update counts and video list
      await loadDashboardData();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete video. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Page actions
  const pageActions = (
    <Button 
      onClick={() => setShowUploadDialog(true)}
    >
      <Upload className="h-4 w-4 mr-2" />
      Upload Video
    </Button>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="text-center py-16">
      <div className="mx-auto w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6">
        <Video className="h-12 w-12 text-blue-500" />
      </div>
      <h3 className="text-xl font-semibold mb-2">
        {searchQuery || statusFilter !== "all" 
          ? "No videos match your search" 
          : "No videos yet"
        }
      </h3>
      <p className="text-muted-foreground mb-8 max-w-md mx-auto">
        {searchQuery || statusFilter !== "all"
          ? "Try adjusting your search terms or filters to find the videos you're looking for."
          : "Start building your video library by uploading your first video."
        }
      </p>
      <div className="flex gap-3 justify-center">
        <Button onClick={() => setShowUploadDialog(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Video
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Use StandardPageHeader like working Families page */}
      <StandardPageHeader
        title="HiveShare Video Management"
        subtitle="Upload and manage videos for your organization"
        icon={Video}
        actions={pageActions}
      />
      
      {/* Use AdminPageScaffold without title props like working Families page */}
      <AdminPageScaffold
        loading={isLoading}
        error={error}
        isEmpty={filteredVideos.length === 0 && !isLoading}
        emptyState={<EmptyState />}
      >
        {/* Usage Overview Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
          <motion.div variants={cardVariants} initial="hidden" animate="visible" custom={0}>
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Videos</CardTitle>
                <FileVideo className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{videos.length}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {videos.filter(v => v.upload_status === 'ready').length} ready to view
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={cardVariants} initial="hidden" animate="visible" custom={1}>
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Storage Used</CardTitle>
                <HardDrive className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {usageStatus?.storage_used_minutes || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  of {usageStatus?.storage_quota_minutes || 100} minutes
                </div>
                {usageStatus && (
                  <Progress 
                    value={usageStatus.usage_percentage} 
                    className="mt-2 h-2"
                  />
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={cardVariants} initial="hidden" animate="visible" custom={2}>
            <Card className="border-l-4 border-l-purple-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Views</CardTitle>
                <Eye className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {videos.reduce((sum, video) => sum + video.view_count, 0)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  across all videos
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={cardVariants} initial="hidden" animate="visible" custom={3}>
            <Card className="border-l-4 border-l-orange-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Processing</CardTitle>
                <Clock className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  {videos.filter(v => v.upload_status === 'processing' || v.upload_status === 'uploading').length}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  videos processing
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search videos by title or description..."
                    aria-label="Search videos"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="uploading">Uploading</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="favorites">Favorites</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Videos List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Videos ({filteredVideos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredVideos.length === 0 ? (
              <div className="text-center py-12">
                <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No videos found</h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Upload your first video to get started'
                  }
                </p>
                {!searchQuery && statusFilter === 'all' && (
                  <Button 
                    onClick={() => setShowUploadDialog(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Video
                  </Button>
                )}
              </div>
            ) : (
              <ScrollArea className="max-h-[600px]">
                <motion.div variants={listVariants} initial="hidden" animate="visible">
                  {filteredVideos.map((video, index) => (
                    <motion.div
                      key={video.id}
                      variants={itemVariants}
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border-b last:border-b-0 hover:bg-gray-50/50 transition-colors"
                    >
                      {/* Video Thumbnail/Icon */}
                      <div className="flex-shrink-0">
                        <AnimatedThumbnail
                          videoId={video.cloudflare_stream_id}
                          title={video.title}
                          className="w-20 h-14 sm:w-28 sm:h-16"
                          breakpoint="tablet"
                          animateOnHover={true}
                          lazy={true}
                          signedToken={signedTokens[video.cloudflare_stream_id]}
                        />
                      </div>

                      {/* Video Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate">{video.title}</h3>
                            {video.description && (
                              <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                                {video.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              {getStatusBadge(video.upload_status)}
                              <span className="text-xs text-gray-500">
                                {formatDuration(video.duration_seconds)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatFileSize(video.file_size_bytes)}
                              </span>
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {video.view_count} views
                              </span>
                              {video.privacy_level === 'selected_families' && (
                                <Badge variant="outline" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  Selected Families
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 self-end sm:self-center">
                        {video.upload_status === 'ready' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              console.log('🎬 Play button clicked for video:', {
                                id: video.id,
                                cloudflare_stream_id: video.cloudflare_stream_id,
                                title: video.title
                              });
                              handlePlayVideo(video);
                            }}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Play</span>
                          </Button>
                        )}
                        
                        {/* Favorite button */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleFavorite(video)}
                          className="p-2"
                        >
                          <Star 
                            className={`h-4 w-4 ${
                              video.is_favorite 
                                ? 'fill-yellow-400 text-yellow-400' 
                                : 'text-gray-400 hover:text-yellow-400'
                            }`} 
                          />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditVideo(video)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            {video.download_url && (
                              <DropdownMenuItem onClick={() => handleDownloadVideo(video)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDeleteVideo(video.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </AdminPageScaffold>

      {/* Dialogs - Outside AdminPageScaffold like working Families page */}
      <VideoUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        organizationId={organizationId}
        families={families.map(f => ({ id: f.id, name: f.name }))}
        onSuccess={handleUploadSuccess}
      />

      <VideoEditDialog
        video={editingVideo}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveVideoEdit}
        families={families.map(f => ({ id: f.id, name: f.name }))}
      />

      <Dialog open={isPlayerModalOpen} onOpenChange={setIsPlayerModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-xl font-semibold">
              {selectedVideo?.title || 'Video Player'}
            </DialogTitle>
            <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {selectedVideo?.duration_seconds 
                  ? `${Math.floor(selectedVideo.duration_seconds / 60)}:${(selectedVideo.duration_seconds % 60).toString().padStart(2, '0')}`
                  : 'Unknown duration'
                }
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {selectedVideo?.view_count || 0} views
              </span>
              <Badge 
                variant={selectedVideo?.upload_status === 'ready' ? 'default' : 'secondary'}
                className="text-xs"
              >
                {selectedVideo?.upload_status || 'unknown'}
              </Badge>
            </div>
          </DialogHeader>
          
          <div className="aspect-video bg-black">
            {selectedVideo && (
              <>
                {console.log('🎬 Modal rendering with selectedVideo:', {
                  id: selectedVideo.id,
                  cloudflare_stream_id: selectedVideo.cloudflare_stream_id,
                  title: selectedVideo.title
                })}
                <AdminVideoPlayer
                  video={selectedVideo}
                  controls
                  autoplay={false}
                  className="w-full h-full"
                />
              </>
            )}
          </div>
          
          {selectedVideo?.description && (
            <div className="p-6 pt-4">
              <h4 className="font-medium text-gray-900 mb-2">Description</h4>
              <p className="text-gray-600 leading-relaxed">{selectedVideo.description}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoManagementDashboardV2;